import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { Kpi, PageHead } from "@/components/Bits";
import { ExportButtons } from "@/components/ExportButtons";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const HqB2BProfit = () => {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("gross_profit");

  useEffect(() => {
    api.get("/reports/hq-b2b").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const t = data.totals;
  const rows = [...data.rows].sort((a, b) =>
    sort === "studio_name" ? a.studio_name.localeCompare(b.studio_name) : b[sort] - a[sort]
  );

  return (
    <div data-testid="hq-b2b-section">
      <PageHead title="HQ Product Profit (B2B)" subtitle={`Material sales to studios · ${data.month}`}>
        <ExportButtons
          testidPrefix="hq-b2b"
          filename="veloskin-hq-b2b-profit"
          title="HQ Product Profit (B2B)"
          subtitle={`Material sales to studios · ${data.month}`}
          columns={[
            { label: "Studio", value: (r) => r.studio_name },
            { label: "Material orders", value: (r) => r.orders },
            { label: "Invoiced to studio", value: (r) => r.invoiced },
            { label: "HQ cost price", value: (r) => r.hq_cost },
            { label: "HQ gross profit", value: (r) => r.gross_profit },
            { label: "Margin %", value: (r) => r.margin_pct },
          ]}
          rows={[
            ...rows,
            {
              studio_name: "NETWORK TOTAL",
              orders: t.orders,
              invoiced: t.invoiced,
              hq_cost: t.hq_cost,
              gross_profit: t.gross_profit,
              margin_pct: t.margin_pct,
            },
          ]}
        />
      </PageHead>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Billed to studios" value={money(t.invoiced)} testid="b2b-invoiced" />
        <Kpi label="HQ cost of materials" value={money(t.hq_cost)} testid="b2b-cost" />
        <Kpi
          label="HQ gross profit"
          value={money(t.gross_profit)}
          testid="b2b-profit"
          accent="text-emerald-600"
        />
        <Kpi label="Margin" value={`${t.margin_pct}%`} testid="b2b-margin" />
      </div>

      <Card className="mt-6 border-brand-line shadow-none">
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">Per studio</CardTitle>
            <p className="mt-1 text-xs text-slate-500">
              What each studio was invoiced for materials, what it cost HQ, and HQ's margin.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              ["gross_profit", "Profit"],
              ["invoiced", "Billed"],
              ["margin_pct", "Margin"],
              ["studio_name", "Studio"],
            ].map(([k, label]) => (
              <button
                key={k}
                data-testid={`b2b-sort-${k}`}
                onClick={() => setSort(k)}
                className={`min-h-[36px] rounded px-3 text-xs font-medium transition-colors duration-200 ${
                  sort === k
                    ? "bg-brand text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="hq-b2b-table" className="mobile-cards">
            <TableHeader>
              <TableRow>
                <TableHead>Studio</TableHead>
                <TableHead className="text-right">Material orders</TableHead>
                <TableHead className="text-right">Invoiced to studio</TableHead>
                <TableHead className="text-right">HQ cost price</TableHead>
                <TableHead className="text-right">HQ gross profit</TableHead>
                <TableHead className="text-right">Margin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.studio_id}
                  className="hover:bg-brand-tint/60"
                  data-testid={`b2b-row-${r.studio_id}`}
                >
                  <TableCell data-label="Studio" className="font-medium">
                    {r.studio_name}
                    <span className="block text-xs text-slate-500">{r.city}</span>
                  </TableCell>
                  <TableCell data-label="Material orders" className="text-right">
                    {r.orders}
                  </TableCell>
                  <TableCell data-label="Invoiced to studio" className="text-right">
                    {money(r.invoiced)}
                  </TableCell>
                  <TableCell data-label="HQ cost price" className="text-right text-slate-500">
                    {money(r.hq_cost)}
                  </TableCell>
                  <TableCell
                    data-label="HQ gross profit"
                    className="text-right font-semibold text-emerald-600"
                    data-testid={`b2b-profit-${r.studio_id}`}
                  >
                    {money(r.gross_profit)}
                  </TableCell>
                  <TableCell data-label="Margin" className="text-right">
                    {r.margin_pct}%
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-50 font-semibold" data-testid="b2b-total-row">
                <TableCell data-label="Studio">Network total</TableCell>
                <TableCell data-label="Material orders" className="text-right">
                  {t.orders}
                </TableCell>
                <TableCell data-label="Invoiced to studio" className="text-right">
                  {money(t.invoiced)}
                </TableCell>
                <TableCell data-label="HQ cost price" className="text-right">
                  {money(t.hq_cost)}
                </TableCell>
                <TableCell data-label="HQ gross profit" className="text-right text-emerald-700">
                  {money(t.gross_profit)}
                </TableCell>
                <TableCell data-label="Margin" className="text-right">
                  {t.margin_pct}%
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
